export const downloadBlobAsFile = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }
};

export type ChartExportSummary = {
  label: string;
  value: string;
  detail?: string;
};

export type ChartExportTableRow = {
  cells: string[];
  color?: string;
};

export type ChartExportContent = {
  title: string;
  subtitle?: string;
  summary?: ChartExportSummary[];
  columns?: string[];
  rows?: ChartExportTableRow[];
  timeAxis?: {
    labels: string[];
    plotLeft: number;
    plotRight: number;
    labelY: number;
  };
  extraChartWidth?: number;
};

const inlineComputedStyles = (source: Element, copy: Element): void => {
  const sourceElements = [source, ...Array.from(source.querySelectorAll('*'))];
  const copyElements = [copy, ...Array.from(copy.querySelectorAll('*'))];

  sourceElements.forEach((sourceElement, index) => {
    const copyElement = copyElements[index];
    if (!copyElement) return;
    const styles = window.getComputedStyle(sourceElement);
    const styleTarget = copyElement as HTMLElement | SVGElement;
    for (let propertyIndex = 0; propertyIndex < styles.length; propertyIndex += 1) {
      const property = styles.item(propertyIndex);
      styleTarget.style.setProperty(property, styles.getPropertyValue(property), styles.getPropertyPriority(property));
    }
  });
};

const loadImage = (source: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('The chart image could not be prepared for download.'));
  image.src = source;
});

const canvasBlob = (canvas: HTMLCanvasElement): Promise<Blob> => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) resolve(blob);
    else reject(new Error('The chart image could not be generated.'));
  }, 'image/png');
});

const escapeXml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const toSvgDataUrl = (source: string): string => URL.createObjectURL(new Blob([source], { type: 'image/svg+xml;charset=utf-8' }));

const downloadRasterizedSvg = async (source: string, width: number, height: number, filename: string): Promise<void> => {
  const svgUrl = toSvgDataUrl(source);
  try {
    const image = await loadImage(svgUrl);
    const pixelRatio = Math.min(2, window.devicePixelRatio || 1);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * pixelRatio);
    canvas.height = Math.round(height * pixelRatio);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Your browser cannot create a chart image.');
    context.scale(pixelRatio, pixelRatio);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    downloadBlobAsFile(await canvasBlob(canvas), filename);
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
};

const removeInteractiveChartElements = (copy: SVGSVGElement): void => {
  copy.querySelectorAll([
    '.flow-analytics__conversation-range-control',
    '.flow-analytics__chart-range-time-label',
    '.flow-analytics__conversation-series-hit-target',
    '.flow-analytics__conversation-hover',
    '.flow-analytics__trend-hover',
    '.flow-analytics__trend-brush'
  ].join(',')).forEach((element) => element.remove());
};

const addStaticTimeAxis = (copy: SVGSVGElement, content: ChartExportContent, width: number): void => {
  const axis = content.timeAxis;
  if (!axis || axis.labels.length === 0) return;
  const namespace = 'http://www.w3.org/2000/svg';
  const right = width - axis.plotRight;
  const tiltLabels = axis.labels.length > 8;
  axis.labels.forEach((label, index) => {
    const text = document.createElementNS(namespace, 'text');
    const progress = axis.labels.length === 1 ? 0 : index / (axis.labels.length - 1);
    const x = axis.plotLeft + (right - axis.plotLeft) * progress;
    text.setAttribute('x', String(x));
    text.setAttribute('y', String(axis.labelY));
    text.setAttribute('fill', '#52616a');
    text.setAttribute('font-family', 'Arial, sans-serif');
    text.setAttribute('font-size', tiltLabels ? '12' : '13');
    text.setAttribute('text-anchor', index === 0 ? 'start' : index === axis.labels.length - 1 ? 'end' : tiltLabels ? 'end' : 'middle');
    if (tiltLabels) text.setAttribute('transform', `rotate(-32 ${x} ${axis.labelY})`);
    text.textContent = label;
    copy.appendChild(text);
  });
};

export const downloadSvgAsPng = async (svg: SVGSVGElement, filename: string): Promise<void> => {
  const viewBox = svg.viewBox.baseVal;
  const rect = svg.getBoundingClientRect();
  const width = Math.max(1, Math.round(viewBox.width || rect.width || 960));
  const height = Math.max(1, Math.round(viewBox.height || rect.height || 540));
  const copy = svg.cloneNode(true) as SVGSVGElement;
  copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  copy.setAttribute('width', String(width));
  copy.setAttribute('height', String(height));
  inlineComputedStyles(svg, copy);

  const serializer = new XMLSerializer();
  await downloadRasterizedSvg(serializer.serializeToString(copy), width, height, filename);
};

/**
 * Creates a static, presentation-ready image of a Flow chart. The exported
 * image deliberately omits interactive controls (the range slider, hover
 * cursor, and card footer), while retaining the current visible chart window.
 */
export const downloadFlowChartAsPng = async (
  svg: SVGSVGElement,
  filename: string,
  content: ChartExportContent
): Promise<void> => {
  const sourceViewBox = svg.viewBox.baseVal;
  const sourceRect = svg.getBoundingClientRect();
  const sourceWidth = Math.max(1, Math.round(sourceViewBox.width || sourceRect.width || 960));
  const sourceHeight = Math.max(1, Math.round(sourceViewBox.height || sourceRect.height || 540));
  const chartWidth = sourceWidth + Math.max(0, content.extraChartWidth ?? 0);
  const copy = svg.cloneNode(true) as SVGSVGElement;
  copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  copy.setAttribute('viewBox', `0 0 ${chartWidth} ${sourceHeight}`);
  copy.setAttribute('width', String(chartWidth));
  copy.setAttribute('height', String(sourceHeight));
  inlineComputedStyles(svg, copy);
  copy.style.removeProperty('width');
  copy.style.removeProperty('height');
  copy.style.removeProperty('margin');
  copy.style.setProperty('overflow', 'visible');
  removeInteractiveChartElements(copy);
  addStaticTimeAxis(copy, content, chartWidth);

  const pageWidth = 1600;
  const horizontalPadding = 64;
  const usableWidth = pageWidth - horizontalPadding * 2;
  const chartHeight = Math.round(usableWidth * sourceHeight / chartWidth);
  const titleHeight = content.subtitle ? 112 : 82;
  const summaryHeight = content.summary?.length ? 76 : 0;
  const chartTop = horizontalPadding + titleHeight + summaryHeight;
  const tableColumns = content.columns ?? [];
  const tableRows = content.rows ?? [];
  const tableTop = chartTop + chartHeight + 32;
  const tableHeaderHeight = tableColumns.length ? 40 : 0;
  const tableRowHeight = 48;
  const tableHeight = tableHeaderHeight + tableRows.length * tableRowHeight;
  const pageHeight = Math.max(320, tableTop + tableHeight + horizontalPadding);
  const serializer = new XMLSerializer();
  const chartMarkup = serializer.serializeToString(copy);

  const summaryMarkup = (content.summary ?? []).map((item, index) => {
    const columnWidth = usableWidth / Math.max(1, content.summary!.length);
    const x = horizontalPadding + index * columnWidth;
    return `<g transform="translate(${x} ${horizontalPadding + titleHeight - 58})">
      ${index > 0 ? `<line x1="-22" x2="-22" y1="0" y2="48" stroke="#d7e1e7"/>` : ''}
      <text x="0" y="14" fill="#66757f" font-family="Arial, sans-serif" font-size="15" font-weight="700">${escapeXml(item.label)}</text>
      <text x="0" y="38" fill="#17212b" font-family="Arial, sans-serif" font-size="24" font-weight="800">${escapeXml(item.value)}</text>
      ${item.detail ? `<text x="0" y="56" fill="#66757f" font-family="Arial, sans-serif" font-size="14">${escapeXml(item.detail)}</text>` : ''}
    </g>`;
  }).join('');

  const firstColumnWidth = tableColumns.length > 1 ? usableWidth * 0.48 : usableWidth;
  const remainingColumnWidth = tableColumns.length > 1 ? (usableWidth - firstColumnWidth) / (tableColumns.length - 1) : 0;
  const columnX = (index: number): number => index === 0
    ? horizontalPadding
    : horizontalPadding + firstColumnWidth + (index - 1) * remainingColumnWidth;
  const cellX = (index: number): number => index === 0
    ? columnX(index) + 20
    : columnX(index) + remainingColumnWidth - 10;
  const tableHeaderMarkup = tableColumns.length ? `<g>
    <rect x="${horizontalPadding}" y="${tableTop}" width="${usableWidth}" height="${tableHeaderHeight}" fill="#f4f7f9"/>
    ${tableColumns.map((column, index) => `<text x="${cellX(index)}" y="${tableTop + 26}" fill="#52616a" font-family="Arial, sans-serif" font-size="14" font-weight="700" text-anchor="${index === 0 ? 'start' : 'end'}">${escapeXml(column)}</text>`).join('')}
  </g>` : '';
  const tableRowsMarkup = tableRows.map((row, rowIndex) => {
    const y = tableTop + tableHeaderHeight + rowIndex * tableRowHeight;
    return `<g>
      <rect x="${horizontalPadding}" y="${y}" width="${usableWidth}" height="${tableRowHeight}" fill="${rowIndex % 2 === 0 ? '#ffffff' : '#fbfcfd'}"/>
      <line x1="${horizontalPadding}" x2="${horizontalPadding + usableWidth}" y1="${y + tableRowHeight}" y2="${y + tableRowHeight}" stroke="#dce5ea"/>
      ${row.color ? `<circle cx="${horizontalPadding + 6}" cy="${y + 24}" r="5" fill="${escapeXml(row.color)}"/>` : ''}
      ${row.cells.map((cell, index) => `<text x="${cellX(index)}" y="${y + 29}" fill="${index === 0 ? '#17212b' : '#1d6ea4'}" font-family="Arial, sans-serif" font-size="16" font-weight="${index === 0 ? '700' : '600'}" text-anchor="${index === 0 ? 'start' : 'end'}">${escapeXml(cell)}</text>`).join('')}
    </g>`;
  }).join('');

  const composite = `<svg xmlns="http://www.w3.org/2000/svg" width="${pageWidth}" height="${pageHeight}" viewBox="0 0 ${pageWidth} ${pageHeight}">
    <rect width="${pageWidth}" height="${pageHeight}" fill="#ffffff"/>
    <text x="${horizontalPadding}" y="${horizontalPadding + 2}" fill="#17212b" font-family="Arial, sans-serif" font-size="32" font-weight="800">${escapeXml(content.title)}</text>
    ${content.subtitle ? `<text x="${horizontalPadding}" y="${horizontalPadding + 34}" fill="#66757f" font-family="Arial, sans-serif" font-size="18" font-weight="600">${escapeXml(content.subtitle)}</text>` : ''}
    ${summaryMarkup}
    <svg x="${horizontalPadding}" y="${chartTop}" width="${usableWidth}" height="${chartHeight}" viewBox="0 0 ${chartWidth} ${sourceHeight}" preserveAspectRatio="xMidYMid meet">${chartMarkup}</svg>
    ${tableHeaderMarkup}
    ${tableRowsMarkup}
  </svg>`;
  await downloadRasterizedSvg(composite, pageWidth, pageHeight, filename);
};
